import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UpperCaseInputDirective } from '@shared/directives';
import { FormErrorComponent } from '@ui-kit/form/form-error/form-error.component';
import { FormLabelComponent } from '@ui-kit/form/form-label/form-label.component';
import { isEqual } from 'lodash-es';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

@Component({
    selector: 'input-text-fc',
    standalone: true,
    imports: [
        FormLabelComponent,
        FormErrorComponent,
        TranslateModule,
        InputTextModule,
        ReactiveFormsModule,
        IconFieldModule,
        InputIconModule,
        NgTemplateOutlet,
        TooltipModule,
        UpperCaseInputDirective,
    ],
    templateUrl: './input-text-fc.component.html',
    styleUrl: './input-text-fc.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTextFcComponent implements OnInit, OnDestroy {
    private translate = inject(TranslateService);

    id = input.required<string>();
    control = input.required<FormControl>();

    label = input<string>('');
    tooltipText = input<string>('');
    required = input<boolean>(false);
    shouldBeUpperCase = input<boolean>(false);

    useTooltipError = input<boolean>(false);
    tooltipErrorPlacement = input<'top' | 'bottom' | 'right' | 'left'>('right');
    tooltipErrorMessages = signal<string[]>([]);

    placeholder = input<string>('uiKit.form.inputText.placeholder');
    name = input<string>(''); //used for validation messages, if there is no label
    size = input<'small' | 'large'>('large');
    type = input<'text' | 'email' | 'password'>('text');

    // used to handle border radius if the button is glued to another element
    glueRight = input<boolean>(false);
    glueLeft = input<boolean>(false);

    onInput = output<void>();
    onBlur = output<void>();

    previousControlStatus = '';

    private readonly subscription: Subscription = new Subscription();

    readonly errorMessages: Record<string, (label: string, control: FormControl, error: any) => string> = {
        maxlength: (label, control) =>
            `${label} ${this.translate.instant('uiKit.form.inputText.maxLengthFormValidator', {
                limit: control.errors?.['maxlength'].requiredLength,
            })}`,
        minlength: (label, control) =>
            `${label} ${this.translate.instant('uiKit.form.inputText.minLengthFormValidator', {
                limit: control.errors?.['minlength'].requiredLength,
            })}`,
        emptyInput: label => `${this.translate.instant('uiKit.form.inputText.emptyInputFormValidator')} ${label}`,
        required: label => `${this.translate.instant('uiKit.form.inputText.emptyInputFormValidator')} ${label}`,
        invalidEmail: () => this.translate.instant('uiKit.form.inputText.emailFormValidator'),
        invalidAlphanumeric: () => this.translate.instant('uiKit.form.inputText.alphanumericValidator'),
        invalidName: () => `${this.translate.instant('uiKit.form.inputText.nameValidator')}`,
        invalidHappyFoxTicketId: () =>
            `${this.translate.instant('uiKit.form.inputText.ckm.setup.ticketNumberValidator')}`,
        probeSequence: () => `${this.translate.instant('uiKit.form.inputText.probeSequenceValidator')}`,
        invalidPrimerSequenceCharacters: () =>
            this.translate.instant('uiKit.form.inputText.invalidPrimerSequenceCharacters'),
        invalidTubeBarcode: () => `${this.translate.instant('uiKit.form.inputNumber.invalidTubeBarcode')}`,
        notOnlyNumbers: () => `${this.translate.instant('uiKit.form.inputNumber.onlyNumbersValidator')}`,
        notMatch: () => `${this.translate.instant('uiKit.form.inputText.mismatchValidator')}`,
    };

    ngOnInit(): void {
        this.handleControlStatusChange();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    inputChange(): void {
        if (this.useTooltipError()) {
            this.computeTooltipErrorMessages();

            this.onInput.emit();
            return;
        }
        this.onInput.emit();
    }

    inputBlur(): void {
        if (this.useTooltipError()) {
            this.computeTooltipErrorMessages();
            this.onBlur.emit();
            return;
        }
        this.onBlur.emit();
    }

    private computeTooltipErrorMessages(): void {
        const errors = this.computeErrors(this.control().errors);
        const label = this.translate.instant(this.label() ? this.label() : this.name());

        const result: string[] = [];
        errors.forEach(error => {
            const r = this.errorMessages[error.name]?.(label, this.control(), error);
            if (r) {
                result.push();
            }
        });

        if (!isEqual(this.tooltipErrorMessages(), result)) {
            this.tooltipErrorMessages.set(result);
        }

        this.onInput.emit();
    }

    handleControlStatusChange() {
        if (this.useTooltipError() && this.control()) {
            this.subscription.add(
                this.control().statusChanges.subscribe(controlStatus => {
                    if (controlStatus !== this.previousControlStatus) {
                        this.previousControlStatus = controlStatus;

                        this.computeTooltipErrorMessages();
                    }
                })
            );
        }
    }

    getErrorKeys(errors: ValidationErrors | null): string[] {
        return errors ? Object.keys(errors) : [];
    }

    getErrorMessage(error: string): string {
        const label = this.label() ? this.label() : this.name();

        return this.errorMessages[error]
            ? this.errorMessages[error](
                  this.translate.instant(label),
                  this.control(),
                  this.control().errors && this.control().errors?.[error]
              )
            : '';
    }

    private computeErrors(errors: ValidationErrors | null): { name: string; optionalInfo?: any }[] {
        return errors
            ? Object.entries(errors).map(([key, value]) => ({
                  name: key,
                  optionalInfo: value,
              }))
            : [];
    }
}
